import React, { useState, useEffect, useContext } from "react";
import PageLayout from "../../../layouts/PageLayout";
import "../../../assets/fonts/ExpenseEntry.css";
import { SidebarContext } from "../../../context/Sidebar";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

/* Robust unique id — safe across dev hot-reloads */
function uid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function makeEmptyCategoryRow() {
  return {
    id: uid(),
    category_id: "",
    amount: "",
  };
}

function makeEmptyPaymentRow() {
  return {
    id: uid(),
    method: "", // "cash" or a bank id
    amount: "",
  };
}

/* ---------------------------------------------------------------- */
/* TEMP dummy data — remove once /expenses-initial API is ready      */
/* ---------------------------------------------------------------- */
const DUMMY_DAILY_CATEGORIES = [
  { id: 1, name: "Tea & Refreshments" },
  { id: 2, name: "Office Supplies" },
  { id: 3, name: "Cleaning Supplies" },
  { id: 4, name: "Fuel Expense" },
  { id: 5, name: "Transport Expense" },
  { id: 6, name: "Courier Charges" },
  { id: 7, name: "Petty Cash Expense" },
  { id: 8, name: "Staff Lunch" },
  { id: 9, name: "Printing & Photocopy" },
  { id: 10, name: "Miscellaneous" },
];
const DUMMY_UTILITY_CATEGORIES = [
  { id: 101, name: "Electricity Bill" },
  { id: 102, name: "Gas Bill" },
  { id: 103, name: "Water Bill" },
  { id: 104, name: "Internet Bill" },
  { id: 105, name: "Telephone Bill" },
  { id: 106, name: "Shop Rent" },
  { id: 107, name: "Generator Fuel" },
  { id: 108, name: "Building Maintenance" },
  { id: 109, name: "Security Charges" },
  { id: 110, name: "Waste Collection Charges" },
];

const DUMMY_BANKS = [
  { id: 1, name: "Meezan Bank - 0123456789" },
  { id: 2, name: "HBL - 9988776655" },
  { id: 3, name: "UBL - 1122334455" },
  { id: 4, name: "Allied Bank - 5566778899" },
  { id: 5, name: "Bank Alfalah - 3344556677" },
];

function validateAndAlert({ expenseDate, categoryRows, paymentRows }) {

  const alert = (msg) => {
    Swal.fire({ icon: "warning", title: "Missing Information", text: msg });
    return false;
  };

  if (!expenseDate) return alert("Please select a date.");

  if (categoryRows.length === 0)
    return alert("Please add at least one expense category.");

  for (let i = 0; i < categoryRows.length; i++) {
    const row = categoryRows[i];
    if (!row.category_id)
      return alert(`Category row ${i + 1}: please select a category.`);
    if (!row.amount || Number(row.amount) <= 0)
      return alert(`Category row ${i + 1}: please enter a valid amount.`);
  }

  if (paymentRows.length === 0)
    return alert("Please add at least one payment method.");

  for (let i = 0; i < paymentRows.length; i++) {
    const row = paymentRows[i];
    if (!row.method)
      return alert(`Payment row ${i + 1}: please select a payment method.`);
    if (!row.amount || Number(row.amount) <= 0)
      return alert(`Payment row ${i + 1}: please enter a valid amount.`);
  }

  const totalCategoryAmount = categoryRows.reduce(
    (sum, r) => sum + (Number(r.amount) || 0),
    0,
  );
  const totalPaymentAmount = paymentRows.reduce(
    (sum, r) => sum + (Number(r.amount) || 0),
    0,
  );

  if (totalCategoryAmount.toFixed(2) !== totalPaymentAmount.toFixed(2)) {
    return alert(
      `Total expense amount (${totalCategoryAmount.toFixed(
        2,
      )}) must match total payment amount (${totalPaymentAmount.toFixed(2)}).`,
    );
  }

  return true;
}

const PAGE_CONFIG = {
  daily: {
    title: "Daily Expense Entry",
    subtitle:
      "Record a daily expense against one or more categories and log how it was paid.",
    eyebrow: "Accounts · Daily Expenses",
    detailTitle: "Daily Expense Details",
    categories: DUMMY_DAILY_CATEGORIES,
  },

  utility: {
    title: "Utility Expense Entry",
    subtitle:
      "Record a utility expense against one or more categories and log how it was paid.",
    eyebrow: "Accounts · Utility Expenses",
    detailTitle: "Utility Expense Details",
    categories: DUMMY_UTILITY_CATEGORIES,
  },
};

/* ---------------------------------------------------------------- */
/* Main component                                                    */
/* ---------------------------------------------------------------- */

export default function ExpenseEntry() {
  const [expenseDate, setExpenseDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [comment, setComment] = useState("");

  const {type} = useParams();



  const config = PAGE_CONFIG[type] || PAGE_CONFIG.daily;

  const [categories, setCategories] = useState(config.categories);
  const [banks, setBanks] = useState(DUMMY_BANKS);

  const [categoryRows, setCategoryRows] = useState(() => [
    makeEmptyCategoryRow(),
  ]);
  const [paymentRows, setPaymentRows] = useState(() => [
    makeEmptyPaymentRow(),
  ]);

  const { setSidebar } = useContext(SidebarContext);
  const navigate = useNavigate();

  useEffect(() => {
    setSidebar(false);
  }, [setSidebar]);


  

  const initialData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/expenses-initial`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      );
      setCategories(response.data?.categories || []);
      setBanks(response.data?.banks || []);
    } catch (error) {
      console.log(error, "error fetching expense initial data");
    }
  };

  useEffect(()=>{
    setCategories(config.categories)
  },[config])
  useEffect(() => {
    initialData();
  }, []);

  /* ---------- category row handlers ---------- */

  const addCategoryRow = () => {
    setCategoryRows((prev) => [...prev, makeEmptyCategoryRow()]);
  };

  const updateCategoryRow = (rowId, patch) => {
    setCategoryRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r)),
    );
  };

  const removeCategoryRow = (rowId) => {
    setCategoryRows((prev) => prev.filter((r) => r.id !== rowId));
  };

  /* ---------- payment row handlers ---------- */

  const addPaymentRow = () => {
    setPaymentRows((prev) => [...prev, makeEmptyPaymentRow()]);
  };

  const updatePaymentRow = (rowId, patch) => {
    setPaymentRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r)),
    );
  };

  const removePaymentRow = (rowId) => {
    setPaymentRows((prev) => prev.filter((r) => r.id !== rowId));
  };

  /* ---------- totals ---------- */

  const totalExpense = categoryRows.reduce(
    (sum, r) => sum + (Number(r.amount) || 0),
    0,
  );
  const totalPayment = paymentRows.reduce(
    (sum, r) => sum + (Number(r.amount) || 0),
    0,
  );

  /* ---------- submit ---------- */

  const handleSubmit = () => {
    const isValid = validateAndAlert({ expenseDate, categoryRows, paymentRows });
    if (!isValid) return;

    const payload = {
      expense_date: expenseDate,
      comment,
      categories: categoryRows.map((r) => ({
        category_id: r.category_id,
        amount: Number(r.amount) || 0,
      })),
      payments: paymentRows.map((r) => ({
        method: r.method, // "cash" or bank id
        amount: Number(r.amount) || 0,
      })),
    };

    axios
      .post(`${process.env.REACT_APP_API_URL}/expenses`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((res) => {
        console.log("res", res);
        Swal.fire({
          icon: "success",
          title: "Saved",
          text: "Expense entry has been recorded.",
        }).then(() => {
          navigate("/expenses");
        });
      })
      .catch((err) => {
        console.log(err, "error saving expense");
        Swal.fire({
          icon: "error",
          title: "Something went wrong",
          text: "Could not save the expense entry.",
        });
      });
  };

  return (
    <PageLayout>
      <div className="pe-scope">
        <div className="pe-page pb-5">
          <div className="container py-4" style={{ maxWidth: "1200px" }}>
            {/* Header */}
            <div className="pe-hero mb-4">
              <span className="pe-eyebrow">{config.eyebrow}</span>
              <h1 className="pe-title">{config.title}</h1>
              <p className="pe-subtitle">
                {config.subtitle}
              </p>
            </div>

            {/* Parent card — holds the two side-by-side sections */}
            <div className="pe-card p-4 mb-4">
              <div className="row g-4">
                {/* LEFT — Expense details + Category & Amount */}
                <div className="col-12 col-lg-7">
                  <div className="pe-inner-card p-3 p-lg-4 h-100">
                    <h5 className="pe-card-title">
                      <i className="fa-solid fa-receipt"></i>{config.detailTitle}
                    </h5>
                    <div className="row g-3 mb-4">
                      <div className="col-12 col-sm-5">
                        <label className="pe-label">Date</label>
                        <input
                          type="date"
                          className="form-control pe-input"
                          value={expenseDate}
                          onChange={(e) => setExpenseDate(e.target.value)}
                        />
                      </div>
                      <div className="col-12 col-sm-7">
                        <label className="pe-label">Comment</label>
                        <input
                          type="text"
                          placeholder="e.g., Shop electricity bill for July"
                          className="form-control pe-input"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h5 className="pe-card-title mb-0">
                        <i className="fa-solid fa-tags"></i> Category &amp;
                        Amount
                      </h5>
                      <span className="pe-hint">
                        Total: <strong>{totalExpense.toFixed(2)}</strong>
                      </span>
                    </div>

                    <div className="d-flex flex-column gap-3">
                      {categoryRows.map((row, index) => (
                        <div className="pe-row-card" key={row.id}>
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="pe-row-tag">
                              Category {index + 1}
                            </span>
                            {categoryRows.length > 1 && (
                              <button
                                type="button"
                                className="pe-ghost-remove pe-ghost-remove-row"
                                onClick={() => removeCategoryRow(row.id)}
                                title="Remove this category row"
                              >
                                X
                              </button>
                            )}
                          </div>

                          <div className="row g-3">
                            <div className="col-12 col-md-7">
                              <label className="pe-label">Category</label>
                              <select
                                className="form-select pe-input"
                                value={row.category_id}
                                onChange={(e) =>
                                  updateCategoryRow(row.id, {
                                    category_id: e.target.value,
                                  })
                                }
                              >
                                <option value="">-- Select Category --</option>
                                {categories.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-12 col-md-5">
                              <label className="pe-label">Amount</label>
                              <input
                                type="number"
                                step="any"
                                placeholder="0.00"
                                className="form-control pe-input"
                                value={row.amount}
                                onChange={(e) =>
                                  updateCategoryRow(row.id, {
                                    amount: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={addCategoryRow}
                      className="pe-add-group-btn mt-3"
                    >
                      <i className="fa-solid fa-circle-plus"></i> Add Category
                    </button>
                  </div>
                </div>

                {/* RIGHT — Payment Method */}
                <div className="col-12 col-lg-5">
                  <div className="pe-inner-card pe-inner-card-accent p-3 p-lg-4 h-100">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h5 className="pe-card-title mb-0">
                        <i className="fa-solid fa-money-bill-wave"></i>{" "}
                        Payment Method
                      </h5>
                      <span className="pe-hint">
                        Total: <strong>{totalPayment.toFixed(2)}</strong>
                      </span>
                    </div>

                    <div className="d-flex flex-column gap-3">
                      {paymentRows.map((row, index) => (
                        <div className="pe-row-card" key={row.id}>
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="pe-row-tag">
                              Payment {index + 1}
                            </span>
                            {paymentRows.length > 1 && (
                              <button
                                type="button"
                                className="pe-ghost-remove pe-ghost-remove-row"
                                onClick={() => removePaymentRow(row.id)}
                                title="Remove this payment row"
                              >
                                X
                              </button>
                            )}
                          </div>

                          <div className="row g-3">
                            <div className="col-12">
                              <label className="pe-label">Pay From</label>
                              <select
                                className="form-select pe-input"
                                value={row.method}
                                onChange={(e) =>
                                  updatePaymentRow(row.id, {
                                    method: e.target.value,
                                  })
                                }
                              >
                                <option value="">
                                  -- Select Bank / Cash --
                                </option>
                                <option value="cash">Cash</option>
                                {banks.map((b) => (
                                  <option key={b.id} value={b.id}>
                                    {b.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-12">
                              <label className="pe-label">Amount</label>
                              <input
                                type="number"
                                step="any"
                                placeholder="0.00"
                                className="form-control pe-input"
                                value={row.amount}
                                onChange={(e) =>
                                  updatePaymentRow(row.id, {
                                    amount: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={addPaymentRow}
                      className="pe-add-group-btn mt-3"
                    >
                      <i className="fa-solid fa-circle-plus"></i> Add Payment
                      Row
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="pe-submit-bar text-end">
              <button
                type="button"
                onClick={handleSubmit}
                className="pe-review-btn"
              >
                <i className="fa-solid fa-check"></i> Save Expense
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}